import { Component, EventEmitter, Inject, Output } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { ConflictImage, UploadOption } from "../../../_objects/inspection-image";
import { IMAGE_FILETYPE } from "../../../app.constants";

export type ConflictImageDialogOptions = {
  applyToAllEnabled: boolean;
  cancelEnabled: boolean;
};

export class ConflictAction {
  image: ConflictImage;
  uploadOption: UploadOption;
  applyToAll: boolean;

  constructor(image: ConflictImage, action: UploadOption, applyToAllRemaining: boolean = false) {
    this.image = image;
    this.uploadOption = action;
    this.applyToAll = applyToAllRemaining;
  }
}

const imageExtensions = [
  IMAGE_FILETYPE.EXTENSION_JPG,
  IMAGE_FILETYPE.EXTENSION_JPEG,
  IMAGE_FILETYPE.EXTENSION_PNG,
  IMAGE_FILETYPE.EXTENSION_BMP,
  IMAGE_FILETYPE.EXTENSION_MP4
];

@Component({
  selector: "app-image-conflict-dialog",
  templateUrl: "./image-conflict-dialog.component.html",
  styleUrls: ["./image-conflict-dialog.component.scss"],
  standalone: false
})
export class ImageConflictDialogComponent {
  public applyToAll: boolean = false;
  public newImagePath: string;

  @Output() dialogAction = new EventEmitter<ConflictAction>();
  @Output() cancelAction = new EventEmitter();

  constructor(
    public dialogRef: MatDialogRef<ImageConflictDialogComponent, { result: boolean }>,
    @Inject(MAT_DIALOG_DATA) public data: { conflictImage: ConflictImage; options?: ConflictImageDialogOptions }
  ) {
    this.extractNewImagePath(data.conflictImage);
  }

  public nextConflict(conflictImage: ConflictImage) {
    this.data.conflictImage = conflictImage;
    this.extractNewImagePath(conflictImage);
  }

  public cancel() {
    this.cancelAction.emit();
  }

  public isImage() {
    const extension = this.getFileExtension(this.data.conflictImage.fileName);
    return imageExtensions.some((i) => extension.includes(extension));
  }

  public get uploadOption() {
    return UploadOption;
  }

  private extractNewImagePath(conflictImage: ConflictImage) {
    if (conflictImage.originalPath) {
      this.newImagePath = conflictImage.originalPath;
    } else if (conflictImage.file) {
      this.readFilePath(conflictImage.file, (path) => (this.newImagePath = path));
    }
  }

  private async readFilePath(file: File, onLoad: (filePath: string) => void) {
    if (this.isVideo()) {
      this.newImagePath = URL.createObjectURL(file);
    } else {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => onLoad(e.target.result as string);
      reader.readAsDataURL(file);
    }
  }

  public action(option: UploadOption) {
    this.dialogAction.emit(new ConflictAction(this.data.conflictImage, option, this.applyToAll));
  }

  public isVideo(): boolean {
    const extension = this.getFileExtension(this.data.conflictImage.fileName);
    return extension?.includes(IMAGE_FILETYPE.EXTENSION_MP4);
  }

  private getFileExtension(filename: string): string | null {
    if (filename.includes(".") && filename.lastIndexOf(".") !== 0) {
      const parts = filename.split(".");
      return parts[parts.length - 1];
    }

    return null;
  }
}
